import { format } from "date-fns";
import _get from "lodash/get";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import CommentsList from "components/lib/Global/CommentsList";
import FormFieldsCard from "components/lib/Global/FormFieldsCard";
import Tabs from "components/lib/Shared/Tabs";
import { Constants } from "config/constants";
import { addComment, editComment } from "state/slices/comment";
import { updateTemplateState } from "state/slices/template";
import AnnualCompliance from "./AnnualCompliance";
import GovernanceSummary from "./GovernanceSummary";
import { tr } from "date-fns/locale";

export default function Summary({ dateFrom, dateTo, isViewMode, transactions }) {
  const [selectedField, setSelectedField] = useState(null);
  const dispatch = useDispatch();
  const { company } = useSelector((state) => state.companySlice);
  const companyData =
    company?.versions?.length > 0
      ? company?.versions[company?.versions?.length - 2] //! I don't know why we need to subtract 2 here john,
      : company; // we subtract 2 because -1 returns the current data
  const { comments } = useSelector((state) => state.commentsSlice);
  const { transactionUnderReview } = useSelector(
    (state) => state.transactionSlice
  );
  const changeOfNameTransaction = transactions.find(
    (trx) =>
      trx.changeType === Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_NAME
  );
  const conversionTransaction = transactions.find(
    (trx) => trx.changeType === Constants.TRANSACTION_CHANGE_TYPES.CONVERSION
  );
  const changeOfFYTransaction = transactions.find(
    (trx) => trx.changeType === Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_FY
  );
  const strikeOffTransaction = transactions.find(
    (trx) => trx.changeType === Constants.TRANSACTION_CHANGE_TYPES.STRIKE_OFF
  );
  const changeOfArticlesTransaction = transactions.find(
    (trx) =>
      trx.changeType === Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_ARTICLES
  );
  const yyyyAnnualReturnsTransaction = transactions.find(
    (trx) =>
      trx.changeType === Constants.TRANSACTION_CHANGE_TYPES.YYYY_ANNUAL_RETURNS
  );
  const yyyyFinancialStatementsTransaction = transactions.find(
    (trx) =>
      trx.changeType ===
      Constants.TRANSACTION_CHANGE_TYPES.YYYY_FINANCIAL_STATEMENTS
  );
  const noticeToCancelConverstionTransaction = transactions.find(
    (trx) =>
      trx.changeType ===
      Constants.TRANSACTION_CHANGE_TYPES.NOTICE_TO_CANCEL_CONVERSION
  );

  const changeOfNameIndex = transactions.findIndex(
    (trx) => trx._id === changeOfNameTransaction?._id
  );
  const conversionIndex = transactions.findIndex(
    (trx) => trx._id === conversionTransaction?._id
  );
  const changeOfFYIndex = transactions.findIndex(
    (trx) => trx._id === changeOfFYTransaction?._id
  );
  const strikeOffIndex = transactions.findIndex(
    (trx) => trx._id === strikeOffTransaction?._id
  );
  const changeOfArticlesIndex = transactions.findIndex(
    (trx) => trx._id === changeOfArticlesTransaction?._id
  );
  const noticeToCancelConverstionIndex = transactions.findIndex(
    (trx) => trx._id === noticeToCancelConverstionTransaction?._id
  );

  const buildUpdateEffectPath = (sliceIndex) =>
    `transactionSlice.transactionUnderReview.items.transactions.${sliceIndex}`;

  const companySummaryComments = comments.filter(
    (comment) => comment.formName === "companySummary"
  );
  const updateEffectKey = "updateTransactionUnderReview";
  const formHelperKey = "initiateTransactionChange";
  const changeOfNameUpdateEffectPath = buildUpdateEffectPath(changeOfNameIndex);
  const conversionUpdateEffectPath = buildUpdateEffectPath(conversionIndex);
  const changeOfFYUpdateEffectPath = buildUpdateEffectPath(changeOfFYIndex);
  const strikeOffUpdateEffectPath = buildUpdateEffectPath(strikeOffIndex);
  const changeOfArticlesUpdateEffectPath = buildUpdateEffectPath(
    changeOfArticlesIndex
  );
  const noticeToCancelConverstionUpdateEffectPath = buildUpdateEffectPath(
    noticeToCancelConverstionIndex
  );

  // comments
  const changeOfNameComments = comments.filter(
    (comment) =>
      comment.transaction === changeOfNameTransaction?._id &&
      comment.formName === "changeOfName"
  );
  const conversionComments = comments.filter(
    (comment) =>
      comment.transaction === conversionTransaction?._id &&
      comment.formName === "conversion"
  );
  const changeOfFYComments = comments.filter(
    (comment) =>
      comment.transaction === changeOfFYTransaction?._id &&
      comment.formName === "changeOfFY"
  );
  const strikeOffComments = comments.filter(
    (comment) =>
      comment.transaction === strikeOffTransaction?._id &&
      comment.formName === "strikeOff"
  );
  const changeOfArticlesComments = comments.filter(
    (comment) => comment.transaction === changeOfArticlesTransaction?._id
  );
  const noticeToCancelConverstionComments = comments.filter(
    (comment) =>
      comment.transaction === noticeToCancelConverstionTransaction?._id
  );

  const tabs = useMemo(() => {
    let tempTabs = [];
    if (
      (changeOfNameTransaction ||
        conversionTransaction ||
        strikeOffTransaction ||
        changeOfFYTransaction ||
        noticeToCancelConverstionTransaction) &&
      !tempTabs.includes({
        id: "company",
        label: "Company Summary",
      })
    ) {
      tempTabs.push({
        id: "company",
        label: "Company Summary",
      });
    }
    if (changeOfArticlesTransaction) {
      tempTabs.push({
        id: "governance",
        label: "Governance Summary",
      });
    }
    if (
      (yyyyAnnualReturnsTransaction || yyyyFinancialStatementsTransaction) &&
      !tempTabs.includes({
        id: "annualCompliance",
        label: "Annual Compliance",
      })
    ) {
      tempTabs.push({
        id: "annualCompliance",
        label: "Annual Compliance",
      });
    }

    return tempTabs;
  }, [
    changeOfNameTransaction,
    conversionTransaction,
    changeOfFYTransaction,
    strikeOffTransaction,
    changeOfArticlesTransaction,
    yyyyAnnualReturnsTransaction,
    yyyyFinancialStatementsTransaction,
    noticeToCancelConverstionTransaction,
  ]);

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [selectedTab, setSelectedTab] = useState(tabs[0]);

  if (!company?._id) return "loading...";

  const { contactPerson, articlesOfAssociation } = company;

  const templateVersions = articlesOfAssociation?.template?.versions ?? [];
  const latestTemplateVersion =
    templateVersions.length > 0
      ? templateVersions[templateVersions.length - 1]
      : [];

  useEffect(() => {
    dispatch(
      updateTemplateState({
        selectedTemplate: articlesOfAssociation?.template,
      })
    );
  }, [articlesOfAssociation?.template]);

  // save different comments
  const handleSaveChangeOfNameComment = async (data) => {
    if (data?.commentId) {
      await dispatch(editComment({ commentId: data.commentId, data }));
    } else {
      await dispatch(
        addComment({
          data: {
            ...data,
            company: company._id,
            transaction: changeOfNameTransaction?._id,
          },
        })
      );
    }
  };

  const handleSaveNoticeToCancelConverstionComment = async (data) => {
    if (data?.commentId) {
      await dispatch(editComment({ commentId: data.commentId, data }));
    } else {
      await dispatch(
        addComment({
          data: {
            ...data,
            company: company._id,
            transaction: noticeToCancelConverstionTransaction?._id,
          },
        })
      );
    }
  };

  const handleSaveConversionComment = async (data) => {
    if (data?.commentId) {
      await dispatch(editComment({ commentId: data.commentId, data }));
    } else {
      await dispatch(
        addComment({
          data: {
            ...data,
            company: company._id,
            transaction: conversionTransaction?._id,
          },
        })
      );
    }
  };
  const handleSaveChangeOfFYComment = async (data) => {
    if (data?.commentId) {
      await dispatch(editComment({ commentId: data.commentId, data }));
    } else {
      await dispatch(
        addComment({
          data: {
            ...data,
            company: company._id,
            transaction: changeOfFYTransaction?._id,
          },
        })
      );
    }
  };
  const handleSaveStrikeOffComment = async (data) => {
    if (data?.commentId) {
      await dispatch(editComment({ commentId: data.commentId, data }));
    } else {
      await dispatch(
        addComment({
          data: {
            ...data,
            company: company._id,
            transaction: strikeOffTransaction?._id,
          },
        })
      );
    }
  };

  useEffect(() => {
    setSelectedTab(tabs[selectedTabIndex]);
  }, [selectedTabIndex]);

  return (
    <>
      <Tabs
        tabs={tabs}
        selectedTabIndex={selectedTabIndex}
        setSelectedTabIndex={setSelectedTabIndex}
        showBorderBottom={false}
      />
      <div>
        {selectedTab.id === "company" && (
          <div className="divide-y-2">
            {changeOfNameTransaction && (
              <div>
                {changeOfNameComments.length > 0 && (
                  <div className="px-8 pt-5 pb-6 mt-4 bg-white">
                    <CommentsList
                      comments={changeOfNameComments}
                      toggleCommentStatus={true}
                    />
                    <p className="mt-6 text-sm text-primary">
                      Comments: {changeOfNameComments.length} of{" "}
                      {changeOfNameComments.length}
                    </p>
                  </div>
                )}
                <FormFieldsCard
                  fields={[
                    {
                      key: "Registration Name",
                      name: "registrationName",
                      value: _get(companyData, "registrationName", "N/A"),
                    },
                    {
                      key: "Abbreviate",
                      name: "isCompanyNameAbbreviated",
                      value: companyData?.isCompanyName1Abbreviated
                        ? "Yes"
                        : "No",
                    },
                    {
                      key: "Special Instruction",
                      name: "specialInstruction",
                      value:
                        companyData?.specialInstructions1 ===
                          "affiliatedBusiness"
                          ? "Affiliated To Existing Business"
                          : company?.specialInstructions1
                            ? company?.specialInstructions1
                            : "None",
                    },
                  ]}
                  hideResolveAllCheckbox
                  addReviewComment={!isViewMode}
                  updateEffectKey={updateEffectKey}
                  updateEffectPath={changeOfNameUpdateEffectPath}
                  formId={_get(changeOfNameTransaction, "_id")}
                  resolvedFields={_get(
                    changeOfNameTransaction,
                    "resolvedFields"
                  )}
                  formHelperKey={formHelperKey}
                  handleSaveComment={handleSaveChangeOfNameComment}
                  comments={changeOfNameComments}
                  formName="changeOfName"
                  changedFields={[
                    {
                      key: "Registration Name",
                      name: "registrationName",
                      value: _get(
                        changeOfNameTransaction?.companyChangedFields,
                        "registrationName",
                        "N/A"
                      ),
                    },
                    {
                      key: "Abbreviate",
                      name: "isCompanyNameAbbreviated",
                      value: _get(
                        changeOfNameTransaction?.companyChangedFields,
                        "isCompanyNameAbbreviated",
                        "N/A"
                      ),
                    },
                  ]}
                  changeType={changeOfNameTransaction?.changeType}
                />
              </div>
            )}

            {conversionTransaction && (
              <div>
                {conversionComments.length > 0 && (
                  <div className="px-8 pt-5 pb-6 mt-4 bg-white">
                    <CommentsList
                      comments={conversionComments}
                      toggleCommentStatus={true}
                    />
                    <p className="mt-6 text-sm text-primary">
                      Comments: {conversionComments.length} of{" "}
                      {conversionComments.length}
                    </p>
                  </div>
                )}
                <FormFieldsCard
                  fields={[
                    {
                      key: "Registration Name",
                      name: "registrationName",
                      value: _get(companyData, "registrationName", "N/A"),
                    },
                    {
                      key: "Registration Number",
                      name: "registrationNumber",
                      value: _get(companyData, "registrationNumber", "N/A"),
                    },
                    {
                      key: "Entity Type",
                      name: "type",
                      value:
                        Constants.ENTITY_TYPE_AGAINST_TYPE[
                        _get(companyData, "type", "N/A")
                        ],
                    },
                  ]}
                  hideResolveAllCheckbox
                  addReviewComment={!isViewMode}
                  updateEffectKey={updateEffectKey}
                  updateEffectPath={conversionUpdateEffectPath}
                  formId={_get(conversionTransaction, "_id")}
                  resolvedFields={_get(conversionTransaction, "resolvedFields")}
                  formHelperKey={formHelperKey}
                  handleSaveComment={handleSaveConversionComment}
                  comments={conversionComments}
                  formName="conversion"
                  changedFields={[
                    {
                      key: "Registration Name",
                      name: "registrationName",
                      value: _get(
                        conversionTransaction?.companyChangedFields,
                        "registrationName",
                        "N/A"
                      ),
                    },
                    {
                      key: "Registration Number",
                      name: "registrationNumber",
                      value: _get(
                        conversionTransaction?.companyChangedFields,
                        "registrationNumber",
                        "N/A"
                      ),
                    },
                    {
                      key: "Entity Type",
                      name: "type",
                      value:
                        Constants.ENTITY_TYPE_AGAINST_TYPE[
                        _get(
                          conversionTransaction?.companyChangedFields,
                          "type",
                          "N/A"
                        )
                        ],
                    },
                  ]}
                  changeType={conversionTransaction?.changeType}
                />
              </div>
            )}

            {strikeOffTransaction && (
              <div>
                {strikeOffComments.length > 0 && (
                  <div className="px-8 pt-5 pb-6 mt-4 bg-white">
                    <CommentsList
                      comments={strikeOffComments}
                      toggleCommentStatus={true}
                    />
                    <p className="mt-6 text-sm text-primary">
                      Comments: {strikeOffComments.length} of{" "}
                      {strikeOffComments.length}
                    </p>
                  </div>
                )}
                <FormFieldsCard
                  wrapperStyles="mt-0"
                  fields={[
                    {
                      key: "Strike Off Application Type",
                      name: "strikeOffApplicationType",
                      value: _get(
                        strikeOffTransaction?.companyChangedFields,
                        "strikeOffApplicationType",
                        "N/A"
                      ),
                    },

                    {
                      key: "Date of Change",
                      name: "dateOfChange",
                      value: _get(
                        strikeOffTransaction?.companyChangedFields,
                        "dateOfChange",
                        "N/A"
                      ),
                    },
                  ]}
                  addReviewComment={!isViewMode}
                  updateEffectKey={updateEffectKey}
                  updateEffectPath={strikeOffUpdateEffectPath}
                  formId={_get(strikeOffTransaction, "_id")}
                  resolvedFields={_get(strikeOffTransaction, "resolvedFields")}
                  formHelperKey={formHelperKey}
                  handleSaveComment={handleSaveChangeOfNameComment}
                  comments={strikeOffComments}
                  formName="strikeOff"
                  changeType={strikeOffTransaction?.changeType}
                />
              </div>
            )}

            {changeOfFYTransaction && (
              <div>
                {changeOfFYComments.length > 0 && (
                  <div className="px-8 pt-5 pb-6 mt-4 bg-white">
                    <CommentsList
                      comments={changeOfFYComments}
                      toggleCommentStatus={true}
                    />
                    <p className="mt-6 text-sm text-primary">
                      Comments: {changeOfFYComments.length} of{" "}
                      {changeOfFYComments.length}
                    </p>
                  </div>
                )}
                <FormFieldsCard
                  wrapperStyles="mt-0"
                  fields={[
                    {
                      key: "Annual Return Date",
                      name: "registrationDate",
                      value: companyData?.registrationDate
                        ? `${format(
                          new Date(companyData?.registrationDate),
                          "dd/MM/yyyy"
                        ).slice(0, -2)}**`
                        : "N/A",
                    },
                    {
                      key: "Financial YE",
                      name: "accountingEndMonth",
                      value: companyData?.primaryBusinessActivity
                        ?.accountingEndMonth
                        ? format(
                          new Date(
                            new Date().getFullYear(),
                            companyData.primaryBusinessActivity
                              .accountingEndMonth - 1,
                            1
                          ),
                          "MMMM"
                        )
                        : "N/A",
                    },
                  ]}
                  addReviewComment={!isViewMode}
                  updateEffectKey={updateEffectKey}
                  updateEffectPath={changeOfFYUpdateEffectPath}
                  formId={_get(changeOfFYTransaction, "_id")}
                  resolvedFields={_get(changeOfFYTransaction, "resolvedFields")}
                  formHelperKey={formHelperKey}
                  handleSaveComment={handleSaveChangeOfNameComment}
                  comments={changeOfFYComments}
                  formName="changeOfFY"
                  changedFields={[
                    {
                      key: "Annual Return Date",
                      name: "registrationDate",
                      value: changeOfFYTransaction?.companyChangedFields
                        ?.registrationDate
                        ? `${format(
                          new Date(
                            changeOfFYTransaction?.companyChangedFields?.registrationDate
                          ),
                          "dd/MM/yyyy"
                        ).slice(0, -2)}**`
                        : "N/A",
                    },
                    {
                      key: "Financial YE",
                      name: "accountingEndMonth",
                      value: changeOfFYTransaction?.companyChangedFields
                        ?.primaryBusinessActivity?.accountingEndMonth
                        ? format(
                          new Date(
                            new Date().getFullYear(),
                            changeOfFYTransaction?.companyChangedFields
                              .primaryBusinessActivity.accountingEndMonth - 1,
                            1
                          ),
                          "MMMM"
                        )
                        : "N/A",
                    },
                  ]}
                  changeType={changeOfFYTransaction?.changeType}
                />
              </div>
            )}
            {noticeToCancelConverstionTransaction && (
              <div>
                <FormFieldsCard
                  wrapperStyles="mt-0"
                  fields={[
                    {
                      key: "Cancellation Date",
                      name: "cancellationDate",
                      value: noticeToCancelConverstionTransaction
                        ?.companyChangedFields?.cancellationDate
                        ? format(
                          new Date(
                            noticeToCancelConverstionTransaction?.companyChangedFields?.cancellationDate
                          ),
                          "yyyy-MM-dd"
                        )
                        : "N/A",
                    },
                    ...noticeToCancelConverstionTransaction?.companyChangedFields?.applicants?.map(
                      (applicant, index) => ({
                        key: `Applicant ${index + 1}`,
                        name: `applicant ${index + 1}`,
                        value:
                          `${applicant?.name} ${applicant?.sharePercentage}% shareholding` ||
                          "N/A",
                      })
                    ),
                  ]}
                  addReviewComment={!isViewMode}
                  updateEffectKey={updateEffectKey}
                  updateEffectPath={noticeToCancelConverstionUpdateEffectPath}
                  formId={_get(noticeToCancelConverstionTransaction, "_id")}
                  resolvedFields={_get(
                    noticeToCancelConverstionTransaction,
                    "resolvedFields"
                  )}
                  formHelperKey={formHelperKey}
                  handleSaveComment={handleSaveNoticeToCancelConverstionComment}
                  comments={noticeToCancelConverstionComments}
                  formName="noticeToCancelConverstion"
                />
              </div>
            )}
          </div>
        )}
        {selectedTab.id === "governance" && (
          <>
            <div className="mt-4">
              <h2 className="font-medium">Articles</h2>

              <GovernanceSummary
                summary={latestTemplateVersion?.summary}
                isViewMode={isViewMode}
              />
              {contactPerson && (
                <FormFieldsCard
                  wrapperStyles="mt-4"
                  hideResolveAllCheckbox
                  fields={[
                    {
                      name: "companyContactName",
                      key: "Full Name",
                      value: contactPerson.fullName || "N/A",
                    },
                    {
                      name: "companyContactDesignation",
                      key: "Designation",
                      value: contactPerson.designation || "N/A",
                    },
                    {
                      name: "telNumber",
                      key: "Telephone Number",
                      value: contactPerson.mobileNumber || "N/A",
                    },
                    {
                      name: "emailAddress",
                      key: "Email Address",
                      value: contactPerson.emailAddress || "N/A",
                    },
                  ]}
                />
              )}
            </div>
          </>
        )}
        {selectedTab.id === "annualCompliance" && <AnnualCompliance />}
      </div>
    </>
  );
}
